import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { type CursorData, DocumentCitationStyle, type DocumentConfig } from '@alpod-ai-writer/shared';

export interface PromptParams {
    document: DocumentV1.Record;
    cursorData: CursorData;
    documentField?: string;
}

export interface CitationPromptParams {
    document: DocumentV1.Record;
}

const Prompt_SystemBase = ({ document, cursorData, documentField }: PromptParams) => {
    const config = document.template?.config as DocumentConfig|undefined;
    const currentSection = config?.sections.find(section => section.id === documentField);

    return `\
Jsi asistent pro psaní studijních podkladů. Pomáháš studentům konzultovat jejich bakalářské, diplomové a dizertační práce.

Uživatel editující dokument: ${cursorData.name}
Dnešní datum: ${new Date()}

# Údaje o práci
Název práce: ${document.documentName}
Typ práce: ${config?.types[document.documentType]?.label ?? '-'}
Citační norma: ${config?.citationStyle}
Instrukce ke psaní práce: ${config?.types[document.documentType]?.aiPrompt ?? '-'}
Aktuální sekce dokumentu: ${currentSection?.title ?? '-'}
Instrukce pro uživatele v této sekci: ${currentSection?.instructions ?? '-'}

# Cíl práce:
${document.goal}

# Pár pravidel:
- Jsi ochoten konzultovat problematiku práce a zamýšlet se nad odborným textem. Nicméně tvým hlavním cílem je pomáhat studentům ve tvorbě obsahu.
- Píšeš spisovně, gramaticky správně, a to v české nebo slovenském jazyce dle jazyku práce, pokud uživatel nevyžádá jinak.
- K formátování můžeš použít Markdown. Podporovány jsou pouze: nadpisy, tučné písmo, kurzíva, podtržené písmo, odrážky.
`;
};

export const Prompt_SystemDefault = (params: PromptParams) => `\
${Prompt_SystemBase(params)}
- Nevypisuj žádné tagy <Blok>, <Výběr>, <Dokument> apod. do výstupu.
`;

export const Prompt_SystemSelecting = (params: PromptParams) => `\
${Prompt_SystemBase(params)}
- <Blok> je aktuální blok, na kterém uživatel pracuje.
- Zajisti, aby se výstup správně vešel do struktury bloku.
- DŮLEŽITÉ: Napiš pouze jeden odstavec textu. Nevytvářej více odstavců.
- <Výběr> je specifický uživatelem vybraný text, který chce upravit nebo zkonzultovat.
- Vem v úvahu celý obsah v <Blok>, ale uprav pouze <Výběr>. Tvá odpověď by měla nahradit obsah ve <Výběr>.
- Nevypisuj žádné tagy <Blok>, <Výběr>, <Dokument> apod. do výstupu.
`;

export const Prompt_SystemBlockSelecting = (params: PromptParams) =>  `\
${Prompt_SystemBase(params)}
- <Výběr> je specifický uživatelem vybraný text, který chce upravit nebo zkonzultovat.
- Tvá odpověď by měla nahradit obsah ve <Výběr>.
- Dodržuj celkovou strukturu a formátování výběru pokud nedostaneš jiné instrukce.
- Nevypisuj žádné tagy <Blok>, <Výběr>, <Dokument> apod. do výstupu.
`;

// --

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Prompt_UserDefault = (params: PromptParams) => `\
<Dokument>
{editor}
</Dokument>

{prompt}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Prompt_UserSelecting = (params: PromptParams) => `\
<Blok>
{block}
</Blok>
<Výběr>
{selection}
</Výběr>

{prompt}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Prompt_UserBlockSelecting = (params: PromptParams) =>  `\
<Výběr>
{block}
</Výběr>

{prompt}
`;

export const Prompt_SystemCitation = (params: CitationPromptParams) => {
    const docConfig = params.document.template?.config as DocumentConfig|undefined;

    const customInstructions = docConfig?.citationCustomInstructions?.trim();

    const parts = [
        `\
${customInstructions
    ? `## CUSTOM FACULTY CITATION FORMAT (primary authority — overrides all base style rules below)

${customInstructions}

---

`
    : ''}Your task is to generate a bibliographic citation for the given source according to the specified citation style${customInstructions ? ' — use the CUSTOM FACULTY FORMAT above as the authoritative reference' : ''}.

## Process (two mandatory steps)

**STEP 1: Gather all bibliographic data**
The source may be given as a book title, ISBN, URL, or a general description. Use tools to find all required fields: author full name, year, publisher, place of publication, ISBN, DOI, etc. Follow this process:
- At the start, use the "note" tool to record: (a) what type of source this is (book, journal, website, etc.), (b) which citation style is required${customInstructions ? ' (CUSTOM FACULTY FORMAT)' : ''}, (c) which fields you need to collect.
- If the source type is **Kapitola z knihy** (book chapter): use the provided ISBN/URL to look up the book's metadata (authors, year, publisher, place, ISBN). The chapter title, chapter authors and page range are already provided by the user — do NOT try to look them up. Combine the book metadata with the user-provided chapter fields to produce the citation.
- Follow the SOURCE HIERARCHY below strictly — always start with source 1 and move to the next only if the current one fails or returns incomplete data.
- After each search, carefully read ALL returned snippets and extract available data before opening any page with openPageAndExtract.
- MANDATORY: Return "Chyba:" ONLY after exhausting ALL 6 levels of the source hierarchy. Never give up after 1–2 failed attempts — try at least 4 different sources.
- MANDATORY: If any required field is missing (e.g. year, publisher, place of publication, author), treat it as a partial failure and move to the next source in the hierarchy — do NOT use a placeholder yet. Only use a placeholder (wrapped in inline code block) after trying ALL sources and the field is still missing.

**STEP 1b: Verify the full publisher name — MANDATORY**
Once you have found the source and identified the publisher name, you MUST verify the full official publisher name:
- Take the publisher name found in step 1 (e.g. "Grada" or "Grada Publishing")
- Search for the publisher's official website and use openPageAndExtract to open their homepage or the book's page on their site
- Extract the full official publisher name as registered (e.g. "Grada Publishing" instead of "Grada")
- Apply citation-style formatting rules to the name (APA 7: strip corporate suffixes like a.s., s.r.o., Inc.; ISO 690: keep full name)
- Use this verified name in the final citation

**STEP 2: Format the data as a citation**
Once you have all the data including the verified publisher name, format it precisely according to the citation style (APA 7, ISO 690, etc.):
- Correct element order (author, year, title, publisher, etc.)
- Correct punctuation and formatting
- Dates and months in the language of the source

Return the result in this exact format:
<citation>
full citation text here
</citation>
<intext>
in-text citation here (just the value, no brackets or parentheses)
</intext>

Rules for the in-text citation value:
- APA 7: 1 author → Novák, 2025 | 2 authors → Novák & Svoboda, 2025 | 3+ authors → Novák et al., 2025
- ISO 690 Harvard: 1 author → NOVÁK, 2025 | 2 authors → NOVÁK a SVOBODA, 2025 | 3+ authors → NOVÁK et al., 2025
- ISO 690 Vancouver / Footnote: leave the intext tag empty — sequential numbering cannot be auto-generated

If the citation cannot be generated, return: "Chyba: <description>. Tento zdroj se nepodařilo automaticky dohledat — vložte citaci prosím ručně."

Additional rules:
- If citing a law, always cite the law itself, not the online source.
- Do not open PDF files or non-HTML documents.

## Source hierarchy (follow in this order)

This hierarchy determines WHERE TO LOOK FOR DATA. The citation format is always governed by the chosen style (APA 7, ISO 690, etc.).

1. **Google Books** — direct URL lookup, deterministic
   - Use openPageAndExtract on https://books.google.com/books?isbn=XXXXXXXXXXXXX (ISBN without hyphens)

2. **WorldCat** — librarian-edited metadata, direct URL lookup
   - Use openPageAndExtract on https://www.worldcat.org/isbn/XXXXXXXXXXXXX

3. **OpenLibrary.org** — direct URL lookup
   - Use openPageAndExtract on https://openlibrary.org/isbn/XXXXXXXXXXXXX

4. **General web search** — search to find the source and any relevant pages
   - Search "ISBN XXXXXXXXXXXXX" in quotes, or "title author year publisher"
   - Carefully read ALL snippets and extract available data before opening any page

5. **Publisher website** — once you know the publisher name (from any step above), open their official website to verify the full publisher name, year, place of publication
   - Use openPageAndExtract on the publisher's book detail page

6. **openPageAndExtract on any relevant page** — last resort
   - Open any page found in step 4 that may contain bibliographic data

Scenarios:
- **Have ISBN**: try 1 → 2 → 3 → 4 → 5
- **Have title + author only**: try 4 (search) → 1 → 2 → 5
- **Online article/website**: 4 (search + snippets) → 6 (extract page)
- **Law**: search official legal portals

**Publisher name verification:** Once you identify the publisher from any source above, verify the full official name by opening the publisher's website. Never rely on shortened names from search snippets alone.

**Publisher name formatting by citation style:**
- APA 7: Omit corporate suffixes (a.s., s.r.o., Inc., Ltd., LLC, GmbH, etc.) — use only the trade name. Example: "Grada Publishing" not "Grada Publishing, a.s."
- ISO 690: Keep the full official name including corporate suffix if it is part of the registered name.

## Publisher name — MANDATORY rules
- NEVER copy a pre-formatted citation from any website (e.g. obalkyknih.cz, databaze-knih.cz). These sites use shortened or incorrect publisher names.
- Always extract RAW bibliographic fields separately — not as a single formatted string.
- If a page shows both a pre-formatted citation AND metadata fields (e.g. "Publisher: Grada Publishing"), always use the metadata field value, never the formatted citation text.
- Use the FULL official publisher name. Examples: "Grada Publishing" not "Grada", "Portál, s.r.o." not "Portál", "Masarykova univerzita" not "MU".
- If you find a shortened name, look up the full name on the publisher's official website.`
    ];

    switch (docConfig?.citationStyle) {
        case DocumentCitationStyle.APA7: {
            parts.push(`\
## Vzorové příklady citací

### APA 7 (autor, rok)

**Autoři v seznamu zdrojů**

1 autor Hýbek, J. (2025).

2 autoři Hýbek, J., & Novák, P. (2025).

3 až 20 autorů Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., Novák, P., Hýbek, J., & Novák, P. (2025).

více než 20 autorů: jednotlivá jména autorů oddělujte čárkami. Po jménech prvních devatenácti autorů použijte trojtečku (výpustku) místo zbývajících autorů. Nakonec uveďte jméno posledního autora (bez použití spojky „a“ nebo znaku „&“ před ním). V citaci by nemělo být uvedeno více než dvacet jmen celkem.

Pegion, K., Kirtman, B. P., Becker, E., Collins, D. C., LaJoie, E., Burgman, R., Bell, R., DelSole, R., Min, D., Zhu, Y., Li, W., Sinsky, E., Guan, H., Gottschalck, J., Metzger, E. J., Barton, N. P., Achuthavarier, D., Marshak, J., Koster, R., . . . Kim, H. (2025).

**Kniha:** Čuřík, J., Maršovská, K., Fojtová, T., & Tomandl, J. (2020). _Krizová komunikace: Principy – zkušenosti – postupy_. Masarykova univerzita.

**Časopis:** Lachlan, K. A., Spence, P. R., & Lin, X. (2014). Expressions of risk awareness and concern through social media: On the utility of focusing on youth during crisis. _Journal of Public Relations Research, 26_(1), 22–40.

**Kapitola v knize**: Susman, E. J., & Dorn, L. D. (2009). Puberty: Its role in development. In R. M. Lerner & L. Steinberg (Eds.), _Handbook of adolescent psychology: Individual bases of adolescent development_ (pp. 116−151). John Wiley & Sons. <https://doi.org/10.1002/9780470479193.adlpsy001006>

**Internetový článek:** Musil, A., & Šindlerová, D. (2022, 15. srpna). _Evakuovaní z Hřenska a okolí se po třech týdnech vracejí domů. Hasiči na místě zůstávají_. Česká televize. <https://ct24.ceskatelevize.cz/clanek/regiony/evakuovani-z-hrenska-a-okoli-se-po-trech-tydnech-vraceji-domu-hasici-na-miste-zustavaji-17356>

**Internetová stránka obecná**: Munipolis. (2025). _Samosprávy_. <https://info.munipolis.cz/samospravy/>

**Sociální média:** Tasty. (2018, March 7). _7 recipes you can make in 5 minutes_ \[Video\]. Youtube. <https://www.youtube.com/watch?v=9_5wHw6l11o>

**Wikipedie:** Quantum mechanics. (2019, November 19). In _Wikipedia_. <https://en.wikipedia.org/w/index.php?title=Quantum_mechanics&oldid=948476810>

**Závěrečné práce:** Špačková, K. (2017). _Digitální marketing v cestovním ruchu - Zvýšení povědomí o České republice jako turistické destinace na zahraničních trzích pomocí sociálních médií_ \[Bakalářská práce\]. Theses.cz. <https://theses.cz/id/wffqbe/>

**Zákon:** Zákon č. 240/2000 Sb., o krizovém řízení a o změně některých zákonů (krizový zákon). (2000).

**Bibliografie (seznam literatúry) — používej POUZE INICIÁLY křestních jmen:**
- Formát: PŘÍJMENÍ, I. — všichni autoři ve stejném formátu, odděleni čárkou, před posledním &
- 1 autor: Hýbek, J. (2025).
- 2 autoři: Hýbek, J., & Novák, P. (2025).
- 3–20 autorů: vypiš všechny. Příklad: Hýbek, J., Novák, P., & Svoboda, K. (2025).
- 21+ autorů: prvních 19, pak . . . , pak poslední (bez &).

**In-text citace — APA 7:**
- 1 autor: Hýbek, 2025
- 2 autoři: Hýbek & Novák, 2025
- 3+ autorů: Hýbek et al., 2025 (vždy et al., nikdy nevypisuj všechny autory)

- Neuváděj ISBN.
            `);
            break;
        }
        case DocumentCitationStyle.ISO690_2011_Harvard: {
            parts.push(`\
### ISO 690:2011 – HARVARD (autor, rok)

**Autoři v seznamu zdrojů** (používej celá jména, ne iniciály)

1 autor HÝBEK, Jakub, 2025.

2 autoři HÝBEK, Jakub a Pavel NOVÁK, 2025.

3 a více HÝBEK, Jakub et al., 2025.

Formát: první autor PŘÍJMENÍ, Jméno — další autoři Jméno PŘÍJMENÍ (obrácené pořadí!).

**Kniha:** ČMEJRKOVÁ, Světla, František DANEŠ a Jindra SVĚTLÁ, 1999. _Jak napsat odborný text_. Praha: Leda. ISBN 80-85927-69-1.

**Časopis:** KLABAN, J. a P. BARTOŠÍK, 2022. Spotové ceny elektřiny. _Automa_. 28(11–12), 34–36. ISSN 1210-0592.

**Kapitola v knize:** MACHÁČKOVÁ, Hana, 2014. Soukromí a sebe-odkrývání na online sociálních sítích. In: ŠEVČÍKOVÁ, Anna. _Děti a dospívající online: vybraná rizika používání internetu_. Praha: Grada, s. 55–74. ISBN 978-80-247-5010-1

**Internetový článek:** BOHÁČEK, P., 2012. Letecká medicína – Smrtící přetížení. In: _Armadninoviny.cz_ \[online\]. 19. 8. 2012 \[cit. 2025-03-11\]. Dostupné z: <https://www.armadninoviny.cz/letecka-medicina-smrtici-pretizeni.html>

**Internetová stránka obecná:** CUTIMED, 2024. O nás. _Cutimed.cz_ \[online\]. © 2024 \[cit. 2024-09-11\]. Dostupné z: <https://www.cutimed.cz/o-nas>

**Sociální média:** Steve Jobs and Steve Ballmer, 2007. In: _Youtu.be_ \[online\]. 27. 2. 2007 \[cit. 2011-05-25\]. Dostupné z: <http://youtu.be/dR8SAFRBmcU>. Kanál uživatele RobinZwama.

**Wikipedie:** Citace, 2011. In: _Wikipedia: the free encyclopedia_ \[online\]. St. Petersburg (Florida): Wikipedia Foundation, 11. 12. 2006, last modified on 25. 4. 2011 \[cit. 2011-05-28\]. Dostupné z: <http://cs.wikipedia.org/wiki/Citace>

**Závěrečné práce:** VAVRYSOVÁ, Alena, 2009. _Produkce karotenoidů kvasinkami rodu Cystofilobasidium_. Brno. Diplomová práce. Vysoké učení technické v Brně, Fakulta chemická, Ústav chemie potravin a biotechnologií.

**Zákon:** Zákon č. 240/2000 Sb., o krizovém řízení a o změně některých zákonů (krizový zákon).

U norem ISO vždy uváděj místo vydání.

Datum uváděj ve formátu YYYY-MM-DD.

Za "In: " uváděj doménu webu, nikoliv název stránky. Tedy např. bbc.com namísto BBC News.
            `);
            break;
        }
        case DocumentCitationStyle.ISO690_2011_Footnote:
        case DocumentCitationStyle.ISO690_2011_Vancouver: {
            parts.push(`\
### ISO 690:2011 – POZNÁMKY POD ČAROU, ČÍSELNÝ ODKAZ

**Autoři v seznamu zdrojů** (používej celá jména, ne iniciály)

1 autor HÝBEK, Jakub.

2 autoři HÝBEK, Jakub a Pavel NOVÁK.

3 a více HÝBEK, Jakub et al.

Formát: první autor PŘÍJMENÍ, Jméno — další autoři Jméno PŘÍJMENÍ (obrácené pořadí!).

**Kniha:** ČMEJRKOVÁ, Světla, František DANEŠ a Jindra SVĚTLÁ. _Jak napsat odborný text_. Praha: Leda, 1999. ISBN 80-85927-69-1.

**Časopis:** KLABAN, Jan a Petr BARTOŠÍK. Spotové ceny elektřiny. _Automa_. 2022, 28(11–12), 34–36. ISSN 1210-0592.

**Kapitola v knize:** MACHÁČKOVÁ, Hana. Soukromí a sebe-odkrývání na online sociálních sítích. In: ŠEVČÍKOVÁ, Anna. _Děti a dospívající online: vybraná rizika používání internetu_. Praha: Grada, 2014, s. 55–74. ISBN 978-80-247-5010-1

**Internetový článek:** BOHÁČEK, P. Letecká medicína – Smrtící přetížení. In: _Armadninoviny.cz_ \[online\]. 19. 8. 2012 \[cit. 2025-03-11\]. Dostupné z: <https://www.armadninoviny.cz/letecka-medicina-smrtici-pretizeni.html>

**Internetová stránka obecná:** CUTIMED. O nás. _Cutimed.cz_ \[online\]. © 2024 \[cit. 2024-09-11\]. Dostupné z: <https://www.cutimed.cz/o-nas>

**Sociální média:** Steve Jobs and Steve Ballmer. In: _Youtu.be_ \[online\]. 27. 2. 2007 \[cit. 2011-05-25\]. Dostupné z: <http://youtu.be/dR8SAFRBmcU>. Kanál uživatele RobinZwama.

**Wikipedie:** Citace. In: _Wikipedia: the free encyclopedia_ \[online\]. St. Petersburg (Florida): Wikipedia Foundation, 11. 12. 2006, last modified on 25. 4. 2011 \[cit. 2011-05-28\]. Dostupné z: <http://cs.wikipedia.org/wiki/Citace>

**Závěrečné práce:** VAVRYSOVÁ, Alena. _Produkce karotenoidů kvasinkami rodu Cystofilobasidium_. Brno, 2009. Diplomová práce. Vysoké učení technické v Brně, Fakulta chemická, Ústav chemie potravin a biotechnologií.

**Zákon:** Zákon č. 240/2000 Sb., o krizovém řízení a o změně některých zákonů (krizový zákon).

Za "In: " uváděj doménu webu, nikoliv název stránky. Tedy např. bbc.com namísto BBC News.
            `);
            break;
        }
        case DocumentCitationStyle.ISO690_2022_Harvard: {
            parts.push(`\
### ISO 690:2022 – HARVARD (autor, rok)

1 autor HÝBEK, Jakub, 2025.

2 až 5 autorů HÝBEK, Jakub; NOVÁK, Pavel; KOVÁČ, Daniel; NOVÁKOVÁ, Anna a ŠRÁMEK, Martin, 2025.

5 a více HÝBEK, Jakub et al., 2025.

Formát ISO 690:2022: všichni autoři ve stejném formátu PŘÍJMENÍ, Celé jméno odděleni středníkem.

**Kniha:** KŘIVOHLAVÝ, Jaro, 1994. _Jak zvládat stres._ Praha: Grada. ISBN 80-7169-121-6.

**Časopis:** FILIP, Miroslav; KOVÁŘ, Dominik; JALŮVKOVÁ, Michaela; JAKALOVÁ, Laura; KEBRLOVÁ, Nikola et al., 2022. Jak souvisí stres, nedůvěra a nedodržování protiepidemických opatření? – Konstruktivistický pohled na negativní jevy pandemické doby. Online. _E-psychologie_. 16(1), 1–30. Dostupné z: <https://doi.org/10.29364/epsy.433>. \[cit. 2023-01-05\].

**Kapitola v knize:** MACHÁČKOVÁ, Hana. Soukromí a sebe-odkrývání na online sociálních sítích. In: ŠEVČÍKOVÁ, Anna. _Děti a dospívající online: vybraná rizika používání internetu_. Praha: Grada, 2014, s. 55–74. ISBN 978-80-247-5010-1

**Internetový článek:** BOHÁČEK, P., 2012. _Letecká medicína – Smrtící přetížení_. Online. In: Armádní noviny. 19. 8. 2012. Dostupné z: <https://www.armadninoviny.cz/letecka-medicina-smrtici-pretizeni.html>. \[cit. 2025-03-11\].

**Internetová stránka obecná:** CUTIMED, 2024. _O nás_. Online. Dostupné z: <https://www.cutimed.cz/o-nas>. \[cit. 2024-09-11\].

**Sociální média:** KOVÁŘ, Karel \[@Kovy\], 2022. _Radikální algoritmy_. Online, video. 2022-03-20. Dostupné z: YouTube, <https://www.youtube.com/watch?v=IARByMNIpsw>. \[cit. 2023-03-15\].

**Wikipedie:** _Pablo Picasso_, 2023_._ Online. In: Wikipedie. Stránka byla naposledy editována 5. 5. 2023 v 12:16. Dostupné z: <https://cs.wikipedia.org/w/index.php?title=Pablo_Picasso&oldid=22766926>. \[cit. 2023-05-15\].

**Závěrečné práce:** MARTINEC, Vladislav, 2018. _Návrh a výroba šachových figurek_. Online, diplomová práce. Praha: České vysoké učení technické v Praze, Fakulta strojní. Dostupné z: <https://dspace.cvut.cz/bitstream/handle/10467/80605/F2-DP-2018-Martinec-Vladislav-DPVladislav%20Martinec.pdf?sequence=-1&isAllowed=y>. \[cit. 2023-03-17\]

**Zákon:** ČESKO, 2012. _Zákon č. 89/2012 Sb., občanský zákoník_. Online. In: Zákony pro lidi. AION CS, © 2010–2023. Dostupné z: <https://www.zakonyprolidi.cz/cs/2012-89>. \[cit. 2023-03-14\].
            `);
            break;
        }
        case DocumentCitationStyle.ISO690_2022_Footnote:
        case DocumentCitationStyle.ISO690_2022_Vancouver: {
            parts.push(`\
### ISO 690: 2022 – POZNÁMKY POD ČAROU, ČÍSELNÝ ODKAZ

**Autoři v seznamu zdrojů** (používej celá jména, ne iniciály)

1 autor HÝBEK, Jakub.

2 až 5 autorů HÝBEK, Jakub; NOVÁK, Pavel; KOVÁČ, Daniel; NOVÁKOVÁ, Anna a ŠRÁMEK, Martin.

5 a více HÝBEK, Jakub et al.

Formát ISO 690:2022: všichni autoři ve stejném formátu PŘÍJMENÍ, Celé jméno odděleni středníkem.

**Kniha:** KŘIVOHLAVÝ, Jaro. _Jak zvládat stres._ Praha: Grada, 1994. ISBN 80-7169-121-6.

**Časopis:** FILIP, Miroslav; KOVÁŘ, Dominik; JALŮVKOVÁ, Michaela; JAKALOVÁ, Laura; KEBRLOVÁ, Nikola et al. Jak souvisí stres, nedůvěra a nedodržování protiepidemických opatření? – Konstruktivistický pohled na negativní jevy pandemické doby. Online. _E-psychologie_. 2022, 16(1), 1–30. Dostupné z: <https://doi.org/10.29364/epsy.433>. \[cit. 2023-01-05\].

**Kapitola v knize:** MACHÁČKOVÁ, Hana. Soukromí a sebe-odkrývání na online sociálních sítích. In: ŠEVČÍKOVÁ, Anna. _Děti a dospívající online: vybraná rizika používání internetu_. Praha: Grada, 2014, s. 55–74. ISBN 978-80-247-5010-1

**Internetový článek:** BOHÁČEK, P. _Letecká medicína – Smrtící přetížení_. Online. In: Armádní noviny. 19. 8. 2012. Dostupné z: <https://www.armadninoviny.cz/letecka-medicina-smrtici-pretizeni.html>. \[cit. 2025-03-11\].

**Internetová stránka obecná:** CUTIMED. _O nás_. Online. © 2024. Dostupné z: <https://www.cutimed.cz/o-nas>. \[cit. 2024-09-11\].

**Sociální média:** KOVÁŘ, Karel \[@Kovy\]. _Radikální algoritmy_. Online, video. 2022-03-20. Dostupné z: YouTube, <https://www.youtube.com/watch?v=IARByMNIpsw>. \[cit. 2023-03-15\].

**Wikipedie:** _Pablo Picasso._ Online. In: Wikipedie. Stránka byla naposledy editována 5. 5. 2023 v 12:16. Dostupné z: <https://cs.wikipedia.org/w/index.php?title=Pablo_Picasso&oldid=22766926>. \[cit. 2023-05-15\].

**Závěrečné práce:** MARTINEC, Vladislav. _Návrh a výroba šachových figurek_. Online, diplomová práce. Praha: České vysoké učení technické v Praze, Fakulta strojní, 2018. Dostupné z: <https://dspace.cvut.cz/bitstream/handle/10467/80605/F2-DP-2018-Martinec-Vladislav-DPVladislav%20Martinec.pdf?sequence=-1&isAllowed=y>. \[cit. 2023-03-17\]

**Zákon:** ČESKO. _Zákon č. 89/2012 Sb., občanský zákoník_. Online. In: Zákony pro lidi. AION CS, © 2010–2023. Dostupné z: <https://www.zakonyprolidi.cz/cs/2012-89>. \[cit. 2023-03-14\].
            `);
            break;
        }
    }

    switch (docConfig?.citationStyle) {
        case DocumentCitationStyle.APA7: {
            parts.push(`\
- If any required field (e.g. year, publisher) cannot be found after trying ALL sources in the hierarchy, use a placeholder in Czech in an inline code block, e.g. \`doplnit rok vydání\`. Only use placeholders as a last resort — always try all sources first.
            `);
            break;
        }
        case DocumentCitationStyle.ISO690_2011_Harvard:
        case DocumentCitationStyle.ISO690_2011_Vancouver:
        case DocumentCitationStyle.ISO690_2011_Footnote:
        case DocumentCitationStyle.ISO690_2022_Harvard:
        case DocumentCitationStyle.ISO690_2022_Vancouver:
        case DocumentCitationStyle.ISO690_2022_Footnote: {
            parts.push(`\
- Always include place of publication for ISO norms.
- Format dates as YYYY-MM-DD.
- If any required field (e.g. place of publication, year) cannot be found after trying ALL sources in the hierarchy, use a placeholder in Czech in an inline code block, e.g. \`doplnit místo vydání\`. Only use placeholders as a last resort — always try all sources first.
            `);
            break;
        }
    }

    parts.push(`\
Always find the full author names — do not use initials. If you cannot find the full first name, wrap the initials in a code block using Markdown, e.g. \`NOVÁK, \`P.\`\`.

---

Current date and time: ${new Date()}`);

    return parts.join('\n');
};

const citationAttemptInstructions: Record<number, string> = {
    2: 'IMPORTANT: This is attempt 2 — the previous attempt failed. Do NOT use Google Books again. Go directly to the publisher website (search for the publisher name + book title) and then try WorldCat: use openPageAndExtract on https://www.worldcat.org/isbn/XXXXXXXXXXXXX.',
    3: 'IMPORTANT: This is attempt 3 — previous attempts failed. Do NOT use Google Books or WorldCat. Try OpenLibrary: use openPageAndExtract on https://openlibrary.org/isbn/XXXXXXXXXXXXX.',
    4: 'IMPORTANT: This is attempt 4 — previous attempts failed. Do NOT use Google Books, WorldCat, or OpenLibrary. Use general web search: search "ISBN XXXXXXXXXXXXX" in quotes, then try different queries like "title author year publisher".',
    5: 'IMPORTANT: This is attempt 5 (last). Try openPageAndExtract on any page that mentions this source — e-shop, library, review, or bibliography. Extract bibliographic data from whatever page you can find.',
};

export const Prompt_UserCitation = (docConfig: DocumentConfig, source: string, attemptNumber = 1) => `\
${docConfig.citationCustomInstructions?.trim()
    ? `Generate a citation according to the CUSTOM FACULTY FORMAT defined in the system prompt (section "Doplňujúce pokyny pre citácie"). Use the custom format examples as the authoritative reference — ignore the ${docConfig.citationStyle} base style wherever it conflicts with the custom instructions.`
    : `Generate a citation according to the ${docConfig.citationStyle} style for the following source.`}

Before returning the citation, use the "note" tool to record: what type of source this is (book, journal, website, etc.), which citation style is required, and what the citation structure should look like with all required fields listed.
${attemptNumber > 1 ? `\n${citationAttemptInstructions[attemptNumber] ?? citationAttemptInstructions[5]}\n` : ''}
Source:
${source}

IMPORTANT: You MUST return the result in exactly this format — both tags are required:
<citation>
full citation here
</citation>
<intext>
in-text citation here (without brackets) — or leave empty for Vancouver/Footnote styles
</intext>
`;

export const Prompt_SystemCitationTranslate = (params: CitationPromptParams) => {
    const docConfig = params.document.template?.config as DocumentConfig|undefined;

    const authorFormat = (() => {
        switch (docConfig?.citationStyle) {
            case DocumentCitationStyle.APA7:
                return 'APA 7: Příjmení, I. (pouze iniciály křestního jména). Příklad: Balakhnin, I.';
            case DocumentCitationStyle.ISO690_2011_Harvard:
            case DocumentCitationStyle.ISO690_2011_Footnote:
            case DocumentCitationStyle.ISO690_2011_Vancouver:
                return 'ISO 690:2011: PŘÍJMENÍ, Celé jméno (příjmení VELKÝMI PÍSMENY, celé křestní jméno). Příklad: BALAKHNIN, Ilja';
            case DocumentCitationStyle.ISO690_2022_Harvard:
            case DocumentCitationStyle.ISO690_2022_Footnote:
            case DocumentCitationStyle.ISO690_2022_Vancouver:
                return 'ISO 690:2022: PŘÍJMENÍ, Celé jméno (příjmení VELKÝMI PÍSMENY, celé křestní jméno). Příklad: BALAKHNIN, Ilja';
            default:
                return 'Zachovej původní formát jména z citace.';
        }
    })();

    return `\
Tvým úkolem je přeložit citaci z azbuky (nebo jiného nelatinkového písma) do latinky a upravit ji dle citační normy.

DŮLEŽITÉ: Nepoužívej žádné nástroje (search, openPageAndExtract, note). Pracuj pouze s textem citace, který dostaneš.

Citační norma: ${docConfig?.citationStyle ?? 'neznámá'}
Požadovaný formát jména autora: ${authorFormat}

## Pravidla pro jména autorů

Transliterace z azbuky (BGN/PCGN — standardní anglická vědecká transliterace):
- х → kh (např. Балахнин → Balakhnin, НЕ Balahnin ani Balakhin)
- ш → sh, щ → shch, ж → zh, ч → ch, ю → yu, я → ya, ё → yo, ц → ts
- й → y (na konci slova: Толстой → Tolstoy), uprostřed: й → y
- ь, ъ → vynech
- Příklady: Балахнин → Balakhnin, Толстой → Tolstoy, Достоевский → Dostoyevsky

Formátuj jméno autora PŘESNĚ podle požadovaného formátu citační normy — to je nejdůležitější pravidlo.

## Pravidla pro název díla

Název díla PŘELOŽ do češtiny — musí to být smysluplný sémantický překlad, NIKDY ne fonetický přepis latinkou.
- Špatně: "Nayden bolee bystryy marshrut" (fonetický přepis — zakázáno)
- Správně: "Nalezen rychlejší postup: Využití map zákaznické cesty ke zvýšení prodeje a loajality" (překlad)
- Pokud existuje oficiální české nebo anglické vydání, použij jeho název.

## Ostatní pravidla

- Zachovej přesně strukturu, interpunkci, kurzívu a formátování původní citace — pouze přelož text, neměň pořadí prvků ani interpunkci.
- Výsledná citace nesmí obsahovat žádné nelatinské znaky.
- Vrať pouze přeloženou citaci, žádný text před nebo za ní.`;
};

export const Prompt_UserCitationTranslate = (citation: string) => `\
Přelož následující citaci do latinky:

${citation}`;
